/***
 *
 *   STEP DRAWER
 *   A right-hand dialog with one step's detail: stage icon, "Stage ·
 *   Workflow" eyebrow, title, body, the "Available to switch on" pill, the
 *   public link (start card only), detail rows (counts pop over) and the
 *   Settings link.
 *
 *   Portalled to document.body at z-index 9: on desktop that sits above the
 *   sidebar (also 9, earlier in the page) and below the support modal (10).
 *   The mobile nav (15) stays above it.
 *
 *   Focus moves to Close when it opens, stays inside while it is open, and
 *   returns to the step that opened it. Esc and a backdrop click close it.
 *
 *   PROPS
 *   open: { trigger, stage, step, isStart }
 *   returnFocus: the element to focus again on close
 *   onClose: close the drawer
 *
 **********/

import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { DetailList, PublicLinkBox, SettingsLink, sentence } from "./details";
import { StageIcon, stageName } from "./stage-icon";
import styles from "./workflow.module.scss";

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
const TITLE_ID = "wf-drawer-title";

export function StepDrawer({ open, returnFocus, onClose }) {
  const closeRef = useRef(null);
  const drawerRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const back = returnFocus;
    closeRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      if (back && typeof back.focus === "function" && document.contains(back)) back.focus();
    };
    // opened once per mount; the parent remounts it for each step
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keep Tab inside the dialog
  function trapTab(e) {
    if (e.key !== "Tab" || !drawerRef.current) return;
    const items = [...drawerRef.current.querySelectorAll(FOCUSABLE)];
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  const { trigger, stage, step, isStart } = open;
  const eyebrow = `${isStart ? "Starts" : stageName(stage)} · ${trigger.title}`;
  const body = isStart ? sentence(step.sub) : step.body;

  return ReactDOM.createPortal(
    <div className={styles.layer}>
      <div
        className={styles.scrim}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <aside
          ref={drawerRef}
          className={styles.drawer}
          role="dialog"
          aria-modal="true"
          aria-labelledby={TITLE_ID}
          onKeyDown={trapTab}
        >
          <button type="button" ref={closeRef} className={styles.close} onClick={onClose}>
            Close
          </button>

          <div className={styles.dh} data-wf={isStart ? undefined : stage}>
            <StageIcon name={isStart ? "start" : stage} large />
            <div>
              <small>{eyebrow}</small>
              <h2 id={TITLE_ID}>{step.title}</h2>
            </div>
          </div>

          {body && <p>{body}</p>}
          {step.state === "available" && (
            <p>
              <span className={styles.pill}>Available to switch on</span>
            </p>
          )}
          {isStart && <PublicLinkBox link={step.public_link} />}
          <DetailList rows={step.details} className={styles.drawerList} />
          {step.settings?.path && (
            <div>
              <SettingsLink settings={step.settings} />
            </div>
          )}
        </aside>
      </div>
    </div>,
    document.body
  );
}
